import React, { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, X } from "lucide-react";
import { BRAND, whatsappHref } from "../data/content";

const BookingCtx = createContext(null);

export const useBooking = () => useContext(BookingCtx);

export const BookingProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openBooking = () => setOpen(true);
  const closeBooking = () => setOpen(false);

  return (
    <BookingCtx.Provider value={{ open, openBooking, closeBooking }}>
      {children}
      <BookingModal open={open} onClose={closeBooking} />
    </BookingCtx.Provider>
  );
};

const BookingModal = ({ open, onClose }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal-overlay"
          className="fixed inset-0 z-[200] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-xl"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            data-testid="booking-modal"
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
            className="relative w-full max-w-2xl border border-[color:var(--border)] bg-[color:var(--surface)]"
          >
            <button
              data-testid="booking-modal-close"
              onClick={onClose}
              className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center border border-[color:var(--border)] text-[color:var(--muted)] transition hover:border-[color:var(--primary)] hover:text-[color:var(--primary)]"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="px-8 pt-10 pb-8 md:px-12 md:pt-14 md:pb-12">
              <div className="chip mb-6">
                <span className="dot" />
                Begin your journey
              </div>
              <h3 className="font-serif text-4xl leading-[0.95] tracking-tight md:text-6xl">
                Choose how you&rsquo;d
                <br />
                <span className="italic-serif text-[color:var(--primary)]">
                  like to reach us.
                </span>
              </h3>
              <p className="mt-4 max-w-md text-sm text-[color:var(--muted)]">
                Message us on Instagram, or tap through to WhatsApp with a
                pre-filled note. Either way — you&rsquo;re already booked in our
                head.
              </p>

              <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
                <BookOption
                  testid="book-instagram"
                  href={BRAND.instagram}
                  icon={<Instagram size={22} />}
                  label="Book via Instagram"
                  sub={BRAND.instagramHandle}
                />
                <BookOption
                  testid="book-whatsapp"
                  href={whatsappHref()}
                  icon={<MessageCircle size={22} />}
                  label="Book via WhatsApp"
                  sub={`+91 ${BRAND.primaryPhone}`}
                  primary
                />
              </div>

              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
                Salon hours — {BRAND.timings}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BookOption = ({ testid, href, icon, label, sub, primary }) => (
  <motion.a
    data-testid={testid}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ y: -3 }}
    className={`group relative flex flex-col items-start gap-3 border p-6 transition-colors ${
      primary
        ? "border-[color:var(--primary)] bg-[color:var(--primary)]/10 hover:bg-[color:var(--primary)]/20"
        : "border-[color:var(--border)] bg-[color:var(--surface-2)] hover:border-[color:var(--primary)]"
    }`}
  >
    <div className="flex h-11 w-11 items-center justify-center border border-[color:var(--border)] text-[color:var(--primary)]">
      {icon}
    </div>
    <div>
      <div className="font-serif text-2xl leading-tight">{label}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[color:var(--muted)]">
        {sub}
      </div>
    </div>
    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--primary)] opacity-0 transition-opacity group-hover:opacity-100">
      Open →
    </div>
  </motion.a>
);

export default BookingModal;
